import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

const EnergyMeterPopover = ({
  activityId,
  activityTitle,
  activityStats,
  majorVersion = '0',
  processVersion = '0.x.x',
  onClose,
  style = {},
}) => {
  const t = useTranslation();
  const tr = (key, fallback) => {
    const val = t(key);
    if (val && val !== key) return val;
    if (fallback !== undefined) return fallback;
    return key;
  };
  const popoverRef = useRef(null);

  const [placement, setPlacement] = useState('below');
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [arrowLeft, setArrowLeft] = useState(70);

  useEffect(() => {
    const updatePosition = () => {
      const nodeElement = document.querySelector(`[data-id="${activityId}"]`);
      const container =
        document.querySelector('.flow-container') || document.querySelector('.publishcontainer');
      if (!nodeElement || !container) return;

      const containerRect = container.getBoundingClientRect();
      const nodeRect = nodeElement.getBoundingClientRect();

      const nodeLeft = nodeRect.left - containerRect.left;
      const nodeTop = nodeRect.top - containerRect.top;
      const nodeWidth = nodeRect.width;
      const nodeHeight = nodeRect.height;

      const popoverWidth = 340; // from CSS
      const popoverHeight = popoverRef.current ? popoverRef.current.offsetHeight : 280;

      // Space check
      const spacing = 12;
      const spaceBelow = containerRect.height - (nodeTop + nodeHeight + spacing);
      const spaceAbove = nodeTop - spacing;

      let chosenPlacement = 'below';
      let calculatedTop = nodeTop + nodeHeight + spacing;

      if (spaceBelow < popoverHeight && spaceAbove > spaceBelow) {
        chosenPlacement = 'above';
        calculatedTop = nodeTop - popoverHeight - spacing;
      }

      // Clamp vertical position so it stays within container bounds
      const maxTop = containerRect.height - popoverHeight - 65; // 65px clearance for bottom banner
      if (calculatedTop > maxTop && maxTop > 10) {
        calculatedTop = maxTop;
      }
      if (calculatedTop < 10) {
        calculatedTop = 10;
      }

      // Horizontal centering
      let calculatedLeft = nodeLeft + nodeWidth / 2 - popoverWidth / 2;

      // Keep inside container bounds
      if (calculatedLeft < 8) {
        calculatedLeft = 8;
      } else if (calculatedLeft + popoverWidth > containerRect.width - 8) {
        calculatedLeft = containerRect.width - popoverWidth - 8;
      }

      // The center of the node relative to the container
      const nodeCenterLeft = nodeLeft + nodeWidth / 2;
      // The center of the node relative to the popover left:
      let calculatedArrowLeft = nodeCenterLeft - calculatedLeft;
      // Keep arrow within popover bounds
      if (calculatedArrowLeft < 15) calculatedArrowLeft = 15;
      if (calculatedArrowLeft > popoverWidth - 15) calculatedArrowLeft = popoverWidth - 15;

      setPlacement(chosenPlacement);
      setCoords({ top: calculatedTop, left: calculatedLeft });
      setArrowLeft(calculatedArrowLeft);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);

    let animationFrameId;
    const checkLoop = () => {
      updatePosition();
      animationFrameId = requestAnimationFrame(checkLoop);
    };
    animationFrameId = requestAnimationFrame(checkLoop);

    return () => {
      window.removeEventListener('resize', updatePosition);
      cancelAnimationFrame(animationFrameId);
    };
  }, [activityId]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) {
        return;
      }
      const nodeEl = e.target.closest('.react-flow__node');
      if (nodeEl) {
        const nodeId = nodeEl.getAttribute('data-id');
        if (nodeId && String(nodeId) === String(activityId)) {
          return;
        }
      }
      onClose();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activityId, onClose]);

  const statsToUse = activityStats || {
    e: 0,
    n: 0,
    d: 0,
    total: 0,
    is_hotspot: false,
    reasons: [],
  };
  const { e = 0, n = 0, d = 0, total = 0, is_hotspot = false, reasons = [] } = statsToUse;

  const ePct = total > 0 ? Math.round((e / total) * 100) : 0;
  const nPct = total > 0 ? Math.round((n / total) * 100) : 0;
  const dPct = total > 0 ? Math.round((d / total) * 100) : 0;

  return (
    <div
      ref={popoverRef}
      className="em-popover"
      style={{
        position: 'absolute',
        zIndex: 1050,
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        ...style,
      }}
    >
      <span
        className="em-popover__arrow"
        style={{
          left: `${arrowLeft}px`,
          top: placement === 'below' ? '-7px' : 'auto',
          bottom: placement === 'above' ? '-7px' : 'auto',
          borderTop: placement === 'below' ? '1px solid var(--color-blue)' : 'none',
          borderLeft: placement === 'below' ? '1px solid var(--color-blue)' : 'none',
          borderBottom: placement === 'above' ? '1px solid var(--color-blue)' : 'none',
          borderRight: placement === 'above' ? '1px solid var(--color-blue)' : 'none',
        }}
      ></span>
      <div className="em-popover__head">
        <div>
          <div className="em-popover__eyebrow">{tr('ENERGY_METER', 'ENERGY METER')}</div>
          <div className="em-popover__title">{activityTitle || 'Activity'}</div>
        </div>
        {is_hotspot && (
          <span className="em-popover__hotspot-badge">
            <i className="fas fa-battery-quarter"></i>
            {tr('HOTSPOT', 'HOTSPOT')}
          </span>
        )}
      </div>

      <div className="em-popover__body">
        <div className="em-popover__section">
          <div className="em-popover__section-label">
            {tr('DISTRIBUTION', 'DISTRIBUTION')} · {total} {tr('RATINGS', 'RATINGS')}
          </div>
          <div className="em-bars">
            <div className="em-bars__row em-bars__row--e">
              <span className="em-bars__label">
                <i className="fas fa-battery-full"></i>
                {tr('ENERGIZING', 'Energizing')}
              </span>
              <div className="em-bars__track">
                <div className="em-bars__fill em-bars__fill--e" style={{ width: `${ePct}%` }}></div>
              </div>
              <span className="em-bars__pct">{ePct}%</span>
            </div>

            <div className="em-bars__row em-bars__row--n">
              <span className="em-bars__label">
                <i className="fas fa-battery-half"></i>
                {tr('NEUTRAL', 'Neutral')}
              </span>
              <div className="em-bars__track">
                <div className="em-bars__fill em-bars__fill--n" style={{ width: `${nPct}%` }}></div>
              </div>
              <span className="em-bars__pct">{nPct}%</span>
            </div>

            <div className="em-bars__row em-bars__row--d">
              <span className="em-bars__label">
                <i className="fas fa-battery-quarter"></i>
                {tr('DRAINING', 'Draining')}
              </span>
              <div className="em-bars__track">
                <div className="em-bars__fill em-bars__fill--d" style={{ width: `${dPct}%` }}></div>
              </div>
              <span className="em-bars__pct">{dPct}%</span>
            </div>
          </div>
        </div>

        {reasons && reasons.length > 0 && (
          <div className="em-popover__section">
            <div className="em-popover__section-label">
              {tr('ANONYMOUS_REASONS', 'ANONYMOUS REASONS')} ({reasons.length})
            </div>
            {reasons.map((r, index) => {
              const ageDays = typeof r.age_days === 'number' ? Math.floor(r.age_days) : 0;
              let ageText = '';
              if (ageDays === 0) {
                ageText = tr('TODAY', 'today');
              } else if (ageDays === 1) {
                ageText = `1 ${tr('DAY_AGO', 'day ago')}`;
              } else {
                ageText = `${ageDays} ${tr('DAYS_AGO', 'days ago')}`;
              }

              return (
                <div
                  key={index}
                  className="em-popover__reason"
                  style={{
                    borderLeftColor:
                      r.vote === 'e' ? '#1F9D55' : r.vote === 'n' ? '#7A8699' : '#FF364A',
                  }}
                >
                  “{r.text}”
                  <div className="em-popover__reason-meta">
                    {tr('ANONYMOUS', 'Anonymous')} · {ageText}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: '14px', fontSize: '9px', color: '#7A8699' }}>
          {tr('AGGREGATED_OVER', 'Aggregated over')}{' '}
          <span className="em-version-chip">v {processVersion || `${majorVersion}.x.x`}</span>
        </div>
      </div>
    </div>
  );
};

export default EnergyMeterPopover;
